/**
 * Demo bootstrap — entry point when the client boots with `?demo=1`.
 *
 * When demo mode is active, `window.fetch` is overridden to intercept
 * every same-origin `/api/<resource>` call and return fixture JSON
 * sourced from `@dataset/seed`. The real React tree mounts normally
 * with TanStack Query — it has no idea the backend is faked.
 *
 * The mock fetch + REST handlers live in
 * `apps/ordo/tools/app-explorer/src/demo/` and are imported here via the
 * `@/demo-mocks` Vite alias.
 *
 * Activated by:
 *   - `?demo=1` in the URL
 *   - localStorage flag `ordo:demo-mode=1` (sticky across navigation)
 *
 * **Important**: Production builds tree-shake this file IF `?demo=1`
 * is never accessed. The fetch override pulls in the seed; the gate
 * must short-circuit before any import happens. The dynamic `import()`
 * call below ensures that.
 */

const DEMO_MODE_KEY = 'ordo:demo-mode'

export function isDemoMode(): boolean {
  if (typeof window === 'undefined') {
    return false
  }
  if (!import.meta.env.DEV) {
    return false
  }
  const params = new URLSearchParams(window.location.search)
  if (params.get('demo') === '1') {
    try {
      window.localStorage.setItem(DEMO_MODE_KEY, '1')
    } catch {
      // ignore — third-party-cookie blockers can throw
    }
    return true
  }
  if (params.get('demo') === '0') {
    try {
      window.localStorage.removeItem(DEMO_MODE_KEY)
    } catch {
      // ignore
    }
    return false
  }
  try {
    return window.localStorage.getItem(DEMO_MODE_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * Install the fetch override. Idempotent — second call is a no-op (the
 * mock guards itself). No Apollo client to return; the real TanStack
 * QueryClient just sees fake JSON on every `/api/...` call.
 */
export async function bootstrapDemo(): Promise<void> {
  // Dynamic import so non-demo bundles tree-shake this away.
  const { installDemoFetch } = await import('@/demo-mocks/mock-fetch')
  installDemoFetch()
}

/**
 * Inject the "Demo data is fake" stripe at the bottom of the viewport.
 * Renders into `document.body` directly (not a React portal) so it
 * works regardless of which page is loaded.
 */
export function installDemoStripe(): void {
  if (typeof document === 'undefined') {
    return
  }
  if (document.getElementById('demo-fake-data-stripe')) {
    return
  }
  const stripe = document.createElement('div')
  stripe.id = 'demo-fake-data-stripe'
  stripe.textContent = '⚡ Demo data — nothing here is real. Reload to reset.'
  Object.assign(stripe.style, {
    position: 'fixed',
    bottom: '0',
    left: '0',
    right: '0',
    height: '24px',
    lineHeight: '24px',
    fontSize: '11px',
    fontFamily: 'system-ui, sans-serif',
    fontWeight: '500',
    textAlign: 'center',
    background: 'linear-gradient(90deg, #6366f1, #a855f7, #ec4899)',
    color: 'white',
    zIndex: '2147483647',
    pointerEvents: 'none',
    letterSpacing: '0.02em',
  } satisfies Partial<CSSStyleDeclaration>)
  document.body.appendChild(stripe)
}
